/* eslint no-unused-vars: ["error", { "varsIgnorePattern": "AskAQuestion" }] */
/* eslint object-shorthand: 0 */

import 'html/include/js/zepto.min';
import 'html/include/js/jquery.unveil';
import 'html/include/js/zepto-anim.min';
import AskAQuestion from '@html/_common/question/script';
import { getCdnUrl, sendRequest } from '@html/_common/utils';

const PLACEHOLDER_ID = 'live-chat';

const EMAIL_SAVED_ACTION = 'boost_email_saved';

const OMNIMOVE_ALLOWED_ACTIONS = [
  EMAIL_SAVED_ACTION,
];

const loadScript = async (url) => new Promise((resolve) => {
  const script = document.createElement('script');
  script.src = url;
  script.async = true;
  script.onload = resolve;
  script.nonce = window.nonce;
  document.body.appendChild(script);
});

const getLocale = () => window.trConfig.domainLocale.replace(/_/g, '-');
const getEnvFilter = () => (window.trConfig.env === 'production' ? 'env:production' : 'env:stage');
const isBookNowPage = () => window.location.pathname.includes('/book-now/');
const isTdpPage = () => window.location.pathname.includes('/t/');
const isEnglishDomain = () => window.trConfig.domainLocale === 'en_US';
const isTabInactive = () => document.visibilityState === 'hidden';
const isTabActive = () => document.visibilityState === 'visible';

export const hasPlaceholderLoaded = () => !!document.getElementById(PLACEHOLDER_ID);
export const hasChatBeenInstantiated = () => !!window.boostAiChat;

const chatStatus = {
  VISIBLE: 'visible',
  CLOSED: 'closed',
  MINIMIZED: 'minimized',
};

function ChatNotifications() {
  const NOTIFICATION_CLASS = '.js-live-chat__notification';
  const NOTIFICATION_TITLE = '\u25CF New message from TourRadar';
  const originalPageTitle = document.title;

  this.notificationIcon = () => $(NOTIFICATION_CLASS);

  this.hasNotifications = () => localStorage.getItem('boostai.conversation.notifications') === 'true';

  this.setNotifications = (display) => localStorage.setItem('boostai.conversation.notifications', display);

  this.setNotificationTitle = () => {
    document.title = NOTIFICATION_TITLE;
  };

  this.setOriginalTitle = () => {
    document.title = originalPageTitle;
  };

  this.addVisibilityListener = () => {
    document.addEventListener('visibilitychange', () => {
      if (isTabActive()) {
        this.setOriginalTitle();
      } else if (this.hasNotifications()) {
        this.setNotificationTitle();
      }
    });
  };

  this.showNotification = () => {
    this.notificationIcon().removeClass('hid');
    this.setNotifications('true');
  };

  this.hideNotification = () => {
    this.notificationIcon().addClass('hid');
    this.setNotifications('false');
  };

  this.playNotificationSound = () => {
    const sound = new Audio(`${getCdnUrl()}images/audio/tap-notification.mp3`);
    sound.onerror = (e) => {
      console.error('Error loading notification sound', e);
    };
    sound.play().catch((e) => {
      console.error('Error playing notification sound', e);
    });
  };

  this.handleNotifications = (isMinimized) => {
    if (isMinimized) {
      this.showNotification();
    }

    if (isTabInactive()) {
      this.setNotificationTitle();
    }

    if (isMinimized || isTabInactive()) {
      this.playNotificationSound();
    }
  };

  this.init = () => {
    this.addVisibilityListener();

    if (this.hasNotifications()) {
      this.showNotification();
    }
  };
}

export default function BoostAiChat(common, trackingParams) {
  this.common = common;

  this.scriptURL = 'https://tourradar.boost.ai/chatPanel/chatPanel.js';

  this.EVENT_CATEGORY = 'boost.ai chat';

  this.conversationId = null;

  this.isMobile = window.innerWidth <= 767;

  /**
   * Default action ID
   * @type {null}
   */
  this.startTriggerActionId = null;

  /**
   * The action ID for BNP flow
   * @type {number}
   */
  this.tdpActionId = 5498;

  /**
   * The action ID for BNP flow
   * @type {number}
   */
  this.bnpActionId = 4228;

  /**
   * The action ID for payment failed flow
   * @type {number}
   */
  this.paymentFailedActionId = 4230;

  /**
   * Load the chat script
   *
   * @private
   */
  this.loadChatScript = async () => {
    await loadScript(this.scriptURL);
  };

  /**
   * Check if chat is visible in localStorage
   * @param status
   * @returns {boolean}
   */
  this.isChatStatus = (status) => localStorage.getItem('boostai.conversation.status') === status;

  /**
   * Check if chat is instantiated in localStorage
   * @returns {boolean}
   */
  this.wasChatInstantiated = () => localStorage.getItem('boostai.chat.instantiated') === 'true';

  /**
   * Store chat being instantiated in localStorage
   *
   * @private
   */
  this.setChatInstantiated = () => localStorage.setItem('boostai.chat.instantiated', 'true');

  /**
   * Update chat status in localStorage
   *
   * @param {string} status
   * @private
   */
  this.setChatStatus = (status) => localStorage.setItem('boostai.conversation.status', status);

  /**
   * Instantiate the chat with custom configuration.
   * It will also attach the events to the chat
   *
   * @private
   */
  this.instantiateChat = () => {
    let startTriggerActionId;
    switch (true) {
      case this.startTriggerActionId !== null:
        startTriggerActionId = this.startTriggerActionId;
        break;
      case isTdpPage() && isEnglishDomain():
        startTriggerActionId = this.tdpActionId;
        break;
      case isBookNowPage():
        startTriggerActionId = this.bnpActionId;
        break;
      default:
        startTriggerActionId = null;
    }
    const options = {
      chatPanel: {
        styling: {
          pace: 'supersonic',
        },
        header: {
          filters: {
            filterValues: [getEnvFilter()],
          },
        },
        settings: {
          startTriggerActionId,
          pageUrl: window.location.href,
          startLanguage: getLocale(),
          conversationId: localStorage.getItem('boostai.conversation.id'),
        },
      },
    };
    this.boost = window.boostInit('TourRadar', options);
    this.attachEvents();
    this.setCustomPayload();
    this.setChatInstantiated();
    window.boostAiChat = this.boost;
  };

  /**
   * Attach events to the chat
   *
   * @private
   */
  this.attachEvents = () => {
    this.boost.chatPanel.addEventListener('EagerContent', (ev) => {
      setTimeout(() => {
        this.boost.chatPanel.triggerAction(ev.detail.next_action_id);
      }, ev.detail.duration_before_timeout);
    });

    this.boost.chatPanel.addEventListener('conversationIdChanged', (ev) => {
      this.conversationId = ev.detail.conversationId;
    });

    this.boost.chatPanel.addEventListener('conversationDeleted', () => {
      this.setCustomPayload();
    });

    this.boost.chatPanel.addEventListener('chatPanelOpened', () => {
      this.setChatStatus(chatStatus.VISIBLE);
      this.chatNotifications.hideNotification();

      setTimeout(() => {
        const conversationIdString = this.getConversationString();
        this.common.sendGoogleEvent(this.EVENT_CATEGORY, 'chat panel opened', conversationIdString, {
          ampEventName: 'live_chat_window_opened',
          chat_id: this.conversationId,
          ...trackingParams,
        });
      }, 2000);
    });

    this.boost.chatPanel.addEventListener('emailSaved', (event) => {
      const email = event.detail.email ?? null;
      this.sendOmnimoveEvent(EMAIL_SAVED_ACTION, email, {
        conversation_id: this.conversationId,
        ip: window.trConfig.customer_ip ?? '',
        country: window.trConfig.customer_country ?? '',
        city: window.trConfig.customer_city ?? '',
        zip: window.trConfig.customer_zipcode ?? '',
      });

      this.common.sendAmplitudeEvent({
        ampEventName: 'live_chat_email_gathered',
        chat_id: this.conversationId,
        email: email,
      });
    });

    this.boost.chatPanel.addEventListener('chatPanelClosed', () => {
      this.setChatStatus(chatStatus.CLOSED);

      const conversationIdString = this.getConversationString();
      this.common.sendGoogleEvent(this.EVENT_CATEGORY, 'chat panel closed', conversationIdString, {
        ampEventName: 'live_chat_window_closed',
        chat_id: this.conversationId,
      });
    });

    this.boost.chatPanel.addEventListener('chatPanelMinimized', () => {
      this.setChatStatus(chatStatus.MINIMIZED);

      const conversationIdString = this.getConversationString();
      this.common.sendGoogleEvent(this.EVENT_CATEGORY, 'chat panel minimized', conversationIdString, {
        ampEventName: 'live_chat_window_minimized',
        chat_id: this.conversationId,
      });
    });

    this.boost.chatPanel.addEventListener('conversationTransferredToHuman', () => {
      const conversationIdString = this.getConversationString();
      this.common.sendGoogleEvent(this.EVENT_CATEGORY, 'transferred to human', conversationIdString, {
        ampEventName: 'live_chat_transferred_to_agent',
        chat_id: this.conversationId,
      });
    });

    this.boost.chatPanel.addEventListener('messageSent', () => {
      const conversationIdString = this.getConversationString();
      this.common.sendGoogleEvent(this.EVENT_CATEGORY, 'conversation started', conversationIdString, {
        ampEventName: 'live_chat_conversation_started',
        chat_id: this.conversationId,
      });
    }, { once: true });

    this.boost.chatPanel.addEventListener('messageReceived', () => {
      this.chatNotifications.handleNotifications(this.isChatStatus(chatStatus.MINIMIZED));
    });

    this.boost.chatPanel.addEventListener('checkAvailability', async () => {
      // Scroll to check availability section
      if (this.isMobile) {
        await this.close();
      }
      $(document).trigger('tdp:goTo', 'check');
    });

    this.boost.chatPanel.addEventListener('askAQuestion', (event) => {
      const tourId = event.detail?.tour_id;
      this.openEnquiryDialog(tourId);
    });

    this.boost.chatPanel.addEventListener('flowStarted', (event) => {
      const { detail } = event;
      const getAmplitudeEventName = () => {
        switch (detail.page ?? null) {
          case 'tdp_question':
            return 'live_chat_boost_tdp_question';
          case 'tour_recommendation':
            return 'live_chat_boost_recommendation';
          default:
            return null;
        }
      };

      const amplitudeEvent = getAmplitudeEventName();

      if (!amplitudeEvent) {
        return;
      }

      this.common.sendAmplitudeEvent({
        ampEventName: amplitudeEvent,
        chat_id: this.conversationId,
      });
    });
  };

  this.setCustomPayload = () => {
    const customPayload = {
      customer_country: window.trConfig.customer_country,
      customer_currency: window.trConfig.customer_currency,
    };

    if (window.trConfig.customer_email) {
      customPayload.customer_email = window.trConfig.customer_email;
      customPayload.customer_name = window.trConfig.customer_name;
    }

    if (isBookNowPage()) {
      customPayload.tour_id = this.common.parameters.tourId;
    }

    this.boost.chatPanel.setCustomPayload(customPayload);
  };

  /**
   * Method that instantiates the chat by checking each
   * step of the initialization process to make sure it will succeed
   *
   * @public
   */
  this.internalInitialization = async () => {
    if (!hasChatBeenInstantiated()) {
      this.chatNotifications = new ChatNotifications();
      this.chatNotifications.init();
      try {
        await this.loadChatScript();
        this.instantiateChat();
      } catch (error) {
        sendRequest('/api/utils/jserror', error);
      }
    }
    // Logic to reuse the chat panel already instantiated
    // from other BoostAiChat objects, as this class isn't just
    // to initialize the chat, but also to interact with it
    if (!this.boost) {
      this.boost = window.boostAiChat;
    }
  };

  /**
   * Attach open event to chat button
   *
   * @public
   */
  this.attachChatButton = () => {
    const chatButton = document.getElementById(PLACEHOLDER_ID);
    if (hasPlaceholderLoaded()) {
      chatButton.addEventListener('click', () => this.open());
    }
  };

  /**
   * Method to open the chat
   *
   * @public
   */
  this.open = async () => {
    await this.internalInitialization();
    this.boost.chatPanel.show();
  };

  /**
   * Method to close the chat
   *
   * @public
   */
  this.close = async () => {
    await this.internalInitialization();
    this.boost.chatPanel.minimize();
  };

  /**
   * Method to trigger a payment failed flow
   *
   * @public
   */
  this.triggerPaymentFailedFlow = async () => {
    await this.triggerFlowByActionId(this.paymentFailedActionId);
    console.log('triggerPaymentFailedFlow called');
  };

  /**
   * Method to trigger a flow by action ID
   *
   * @public
   */
  this.triggerFlowByActionId = async (actionId) => {
    if (!hasChatBeenInstantiated()) {
      this.startTriggerActionId = actionId;
      await this.open();
      return;
    }

    this.boost.chatPanel.triggerAction(actionId);
    this.boost.chatPanel.show();
  };

  this.getConversationString = () => `${window.location.href} - conversation ID: ${this.conversationId}`;

  this.openEnquiryDialog = (tourId = -1) => {
    const openAskAQuestion = () => {
      const question = new AskAQuestion();
      question.startEnquiryDialog({ id: tourId });
    };

    if (typeof AskAQuestion !== 'undefined') {
      openAskAQuestion();
    } else {
      this.common.loadJs(this.common.scriptUrl.askAQuestion, openAskAQuestion);
    }
  };

  this.init = () => {
    this.attachChatButton();

    if (!this.wasChatInstantiated()) {
      return;
    }

    if (this.isChatStatus(chatStatus.VISIBLE) && !this.isMobile) {
      this.open();
      return;
    }

    if (isBookNowPage()) {
      this.common.byUserInteraction().then(() => {
        this.internalInitialization();
      });
    } else {
      this.common.byUserInteraction('chat-initialize', () => {
        this.internalInitialization();
      });
    }
  };

  this.sendOmnimoveEvent = (action, email, data) => {
    if (!OMNIMOVE_ALLOWED_ACTIONS.includes(action)) {
      return;
    }

    if (typeof email !== 'string') {
      return;
    }

    $.ajax({
      type: 'POST',
      url: `/api/omnimove/action/${action}`,
      data: {
        data: {
          email: email,
          ...(data ?? {}),
        },
      },
    });
  };

  this.getCurrentDate = () => {
    const date = new Date();
    const iso = date.toISOString().match(/(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2}:\d{2})/);

    return `${iso[1]} ${iso[2]}`;
  };
}
